// TanStack Router pathless layout guard (T04 §9.5): every route under routes/_authenticated/ needs a
// session. Anonymous users are sent to /login with the original URL. The idle timer (CR-4) and the
// user menu (Log out) live here so every authenticated page has them.
import * as React from "react";
import {
  createFileRoute,
  redirect,
  Link,
  Outlet,
  useNavigate,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { applyRouteChange } from "@/features/shell/route-change";
import { isSwitchingScreen, screenNameOf } from "@/features/shell/route-switch";
import { useBuildInfo } from "@/features/shell/use-build-info";
import { userAccessLabel } from "@/features/shell/user-access-label";
import productMarkUrl from "@/assets/favicon/top-bar-mark.png";
import { Button } from "@vbltc/ui/button";
import { TopBar } from "@vbltc/ui/components/top-bar/top-bar";
import { SideNav } from "@vbltc/ui/components/side-nav/side-nav";
import { PageHeader } from "@vbltc/ui/components/page-header/page-header";
import { StatusRegion } from "@vbltc/ui/components/status-region/status-region";
import { BuildMarker } from "@vbltc/ui/components/build-marker/build-marker";
import { isRoleName, roleLabel, type Permissions } from "@vbltc/schema/permissions";
import { uiDocumentSchema } from "@vbltc/schema";
import { metadataQueryOptions } from "@/features/aco-shell/api/metadata";
import { uiConfigQueryOptions } from "@/features/aco-shell/api/ui-config";
import { useRecalculate } from "@/features/aco-shell/api/use-recalculate";
import { AcoContextBar } from "@/features/aco-shell/components/aco-context-bar";
import { ScreenAccessNotice } from "@/features/aco-shell/components/screen-access-notice";
import { ShellLoading } from "@/features/aco-shell/components/shell-loading";
import { screenAccess } from "@/features/aco-shell/screen-access";
import { ALL_ACOS, useAcoContext } from "@/features/aco-shell/hooks/use-aco-context";
import {
  buildNavigation,
  gapUploadsNavSection,
  inSectionOrder,
  navigationIdsForScope,
  navSectionOf,
  showsAcoWideNav,
} from "@/features/aco-shell/navigation";
import { navLinkSearch } from "@/features/aco-shell/aco-search";
import {
  acoHasData,
  defaultAcoCode,
  isProduction,
  knownNotProduction,
  landingPath,
} from "@/features/aco-shell/default-aco";
import { endingSession } from "@/api/resilient-fetch";
import { meAccessQueryOptions } from "@/features/auth/api/me-access";
import { grantedAcoCodes, isGroupScoped, isOwnScoped } from "@/lib/group-access";
import { themeCss } from "@/lib/theme";
import { authClient } from "../features/auth/client";
import {
  ClipboardList,
  Columns3,
  Database,
  FileClock,
  FileUp,
  Gauge,
  LayoutGrid,
  Mail,
  Settings,
  ShieldCheck,
  Stethoscope,
  Table2,
  Users,
} from "lucide-react";
import { useIdleLogout } from "../features/auth/use-idle-logout";
import { readSession, SessionUnavailableError } from "../features/auth/session-read";
import { rememberSignedInUser } from "../features/auth/return-to";
import { mustChangePassword } from "../features/auth/password-change";
import { useServerBusy } from "@/api/use-server-busy";
import { InShellContext } from "@/app/in-shell";
import { setTelemetryUser } from "../telemetry";
import { acoContextSearchSchema } from "@/lib/route-search";

// J-059: the ACO screens' year and ACO live in the URL, validated here for every child route; J-216:
// a value that fails its check is dropped (`lib/route-search.ts`).

export const Route = createFileRoute("/_authenticated")({
  validateSearch: acoContextSearchSchema,
  beforeLoad: async ({ location }) => {
    // J-210 (inbox 06 PR E point 11): only "no session" or a 401 is signed out. A 429 (after the
    // fetch has waited out Retry-After) or a 5xx is "the server could not answer": the session is
    // still open, so the page says busy instead of showing the sign-in form.
    const read = await readSession(() => authClient.getSession());
    if (read.kind === "unavailable") throw new SessionUnavailableError(read.status);
    if (read.kind === "signed-out") {
      // J-200: the Playground is never a page to return to (production has none); sign-in lands on the
      // default ACO instead.
      const back = location.pathname === "/playground" ? undefined : location.href;
      throw redirect({ to: "/login", search: { redirect: back, reason: undefined } });
    }
    const session = read.session;
    // J-244: an account that must change its password reaches nothing else; the server refuses it too.
    if (mustChangePassword(session)) throw redirect({ to: "/change-password" });
    setTelemetryUser(session.user.id); // opaque id only (T7/T12)
    // J-243: whose page this tab is on, so a sign-in by someone else never returns to it.
    rememberSignedInUser(session.user.id);
    return { session };
  },
  errorComponent: SessionErrorView,
  component: AuthenticatedLayout,
});

/** The layout's error view: "busy" for a session the server could not read, else the error's words. */
function SessionErrorView({ error, reset }: { error: unknown; reset: () => void }) {
  const router = useRouter();
  const busy = error instanceof SessionUnavailableError;
  return (
    <main className="mx-auto flex min-h-screen max-w-[720px] flex-col justify-center gap-4 p-6">
      <StatusRegion
        tone="moderate"
        message={
          error instanceof Error ? error.message : "The page could not be loaded. Try again."
        }
        className="rounded-md px-4"
      />
      <Button
        className="justify-self-start"
        data-telemetry={busy ? "session-busy-retry" : "error-retry"}
        onClick={() => {
          reset();
          void router.invalidate();
        }}
      >
        Try again
      </Button>
    </main>
  );
}

/**
 * The system navigation, outside production only (CR-005 item 4, J-191). Every entry points at a route that exists — a nav item is a promise that a
 * screen is there, and an item pointing at an unregistered route sends a click out of the
 * application to a 404 (J-057). The ACO module's items come from `ui.yaml` (J-059): one whose screen
 * is not built yet is shown and not linked, until the item that builds it registers its route.
 */
const NAV_ITEMS = [
  {
    id: "playground",
    label: "Playground",
    href: "/playground",
    section: "System",
    icon: <LayoutGrid />,
  },
];

/** Screen 10's pages (J-073; J-408 Email setup), shown to a role holding each one's permission. */
const ADMIN_NAV_ITEMS = [
  {
    id: "admin-users",
    label: "Users & Access",
    href: "/admin/users",
    section: "Admin",
    icon: <Users />,
    permissions: { acoAdmin: ["manageAccess"] },
  },
  // J-408 (Louis, 2026-11-02 1:13 PM ET, item 2): in the Users & Access area, admin only.
  {
    id: "admin-email",
    label: "Email setup",
    href: "/admin/email",
    section: "Admin",
    icon: <Mail />,
    permissions: { acoAdmin: ["manageAccess"] },
  },
  {
    id: "admin-audit",
    label: "Audit log",
    href: "/admin/audit",
    section: "Admin",
    icon: <FileClock />,
    permissions: { acoAdmin: ["readAudit"] },
  },
] as const;

const ACO_NAV_ICONS: Readonly<Record<string, React.ReactNode>> = {
  groups: <Table2 />,
  aco_summary: <Gauge />,
  compare: <Columns3 />,
  data_quality: <ShieldCheck />,
  data: <Database />,
  settings: <Settings />,
  practice: <Stethoscope />,
  practice_gaps: <ClipboardList />,
};

function AuthenticatedLayout() {
  const { session } = Route.useRouteContext();
  const router = useRouter();
  const role = session.user.role;
  // The same statement as the server's guards (T04 §9.5); the server stays the authority.
  const can = (permissions: Permissions) =>
    isRoleName(role) &&
    authClient.admin.checkRolePermission({
      role,
      permissions: permissions as Record<string, string[]>,
    });
  // Metadata and the ui document are read by every role but clinician (J-034, J-042).
  const readsShell = can({ acoQuality: ["read"] }) || can({ acoPractice: ["read"] });
  const uiConfig = useQuery({ ...uiConfigQueryOptions(), enabled: readsShell });
  const metadata = useQuery({ ...metadataQueryOptions(), enabled: readsShell });
  const ui = uiDocumentSchema.safeParse(uiConfig.data?.document);
  const acoContext = useAcoContext(metadata.data);
  const recalculate = useRecalculate();
  const appName = ui.success ? ui.data.branding.app_name : "VBLTC";
  // Branding colour overrides set the design system's tokens on :root, never an element (J-072).
  const theme = ui.success ? themeCss(":root", ui.data.branding) : "";
  // CR-005 item 3 (J-191): "All" opens the first ACO with data, never a retired or empty one.
  const summaryAco =
    acoContext.aco === ALL_ACOS
      ? defaultAcoCode(metadata.data?.acos ?? [], acoContext.year)
      : acoContext.aco;
  const access = useQuery(meAccessQueryOptions());
  // J-238 (group access), J-251 (the owner's order item 9): a group or practice user's menu holds only
  // the screens served to its scope. Until `/me/access` answers, the ACO-wide items wait, so such a user
  // never sees them flash; a failed read shows the role's menu (the server refuses what the scope may not
  // open either way).
  const ownScoped = access.isPending || isOwnScoped(access.data);
  // J-266: a failed read is "failed", not an ACO-set user's: the screen gate shows its error state with
  // Retry on a screen of ACO-wide data instead of opening it (the menu above still falls back to the role).
  const scopeKind = access.data?.scopeKind ?? (access.isError ? "failed" : undefined);
  const acoNav = buildNavigation(
    navigationIdsForScope(ui.success ? ui.data.navigation : [], ownScoped),
    can,
    (path) => path in router.routesByPath,
  ).map((entry) => ({
    id: entry.id,
    label: entry.label,
    // J-200: with no ACO with data there is no summary to open; the link goes where sign-in lands.
    href:
      entry.path.includes("$acoCode") && summaryAco === undefined
        ? landingPath(metadata.data, can({ acoData: ["load"] }))
        : entry.path.replace("$acoCode", summaryAco ?? ""),
    // J-280 (QB3 item 6d): Data Quality and Data under Data, Settings under Admin, the rest under ACO.
    section: navSectionOf(entry.id),
    icon: ACO_NAV_ICONS[entry.id],
    available: entry.available,
  }));
  const unavailable = new Set(acoNav.filter((item) => !item.available).map((item) => item.href));
  // J-320: "Gap Workbook Uploads", an uploader's under ACO and the admin's under Admin, given PHI access.
  const gapUploadsSection = gapUploadsNavSection(can, access.data?.phiAccess, ownScoped);
  const gapUploadsNav =
    gapUploadsSection === undefined
      ? []
      : [
          {
            id: "gap-workbook-uploads",
            label: "Gap Workbook Uploads",
            href: "/gap-workbook-uploads",
            section: gapUploadsSection,
            icon: <FileUp />,
          },
        ];
  // J-280 (QB3 item 6d): sections ACO, Data, Admin, System, in that order. The admin pages go in
  // before the configured items so that, sorted stably by section, Settings follows Audit log. J-320:
  // Gap Workbook Uploads goes in last of the admin pages, or last of the ACO section for an uploader.
  const navItems = inSectionOrder([
    ...ADMIN_NAV_ITEMS.filter((item) => showsAcoWideNav(ownScoped) && can(item.permissions)).map(
      (item) => ({
        id: item.id,
        label: item.label,
        href: item.href,
        section: item.section,
        icon: item.icon,
      }),
    ),
    ...gapUploadsNav.filter((item) => item.section === "Admin"),
    ...acoNav.map((item) => ({
      id: item.id,
      label: item.label,
      href: item.href,
      section: item.section,
      icon: item.icon,
    })),
    ...gapUploadsNav.filter((item) => item.section === "ACO"),
    // Shown once metadata says this is not production, so production never flashes it.
    ...(knownNotProduction(metadata.data) && showsAcoWideNav(ownScoped) ? NAV_ITEMS : []),
  ]);
  // J-209 (inbox 06 PR D item 13): the list marks an ACO without results for the year.
  // J-238: a group user's list holds only its groups' ACOs (the server scopes metadata too).
  const headerAcos = (metadata.data?.acos ?? [])
    .filter(
      (a) =>
        !isGroupScoped(access.data) ||
        grantedAcoCodes(access.data?.groups ?? []).includes(a.acoCode),
    )
    .map((a) => ({
      acoCode: a.acoCode,
      acoName: a.acoName,
      hasData: acoHasData(a, acoContext.year),
    }));
  const navigate = useNavigate();
  const { warning, stayActive } = useIdleLogout();
  const busy = useServerBusy();
  const build = useBuildInfo();
  // The rail's collapsed state lives here, not in SideNav: the component is presentational and a
  // consumer may want to persist the choice. Without this the Collapse button rendered and did
  // nothing, because `onToggleCollapsed` was never passed (J-057).
  const [navCollapsed, setNavCollapsed] = React.useState(false);

  // The screen's own name and path, read from the deepest matched route. This layout stays mounted
  // while the outlet swaps children, so anything derived from "the current screen" has to come from
  // router state — a value written here would be pinned to whichever screen was first.
  const { pathname, href, screenName, requires, ownScope, switching, locationSearch } =
    useRouterState({
      select: (state) => {
        // Leaf first: the deepest route that names a screen wins, and a route that declares nothing
        // inherits from its parent instead of falling back to a placeholder.
        const leafFirst = [...state.matches].reverse();
        const named = leafFirst.find((m) => m.staticData?.screenName);
        // J-232 (inbox file 33, item 5a; B1): from the click until the next screen has loaded, the
        // matches are still the old screen's; the heading names the screen the URL already names.
        const switching = isSwitchingScreen({
          status: state.status,
          pathname: state.location.pathname,
          resolvedPathname: state.resolvedLocation?.pathname,
        });
        return {
          pathname: state.location.pathname,
          href: state.location.href,
          screenName:
            (switching
              ? screenNameOf(router.getMatchedRoutes(state.location.pathname)[0])
              : undefined) ??
            named?.staticData?.screenName ??
            "VBLTC",
          requires: leafFirst.find((m) => m.staticData?.requires)?.staticData?.requires,
          // J-251: the leaf screen's own flag; a screen that does not declare it shows ACO-wide data.
          ownScope: named?.staticData?.ownScope === true,
          switching,
          // The URL's search, which the navigation links carry on (the old screen's match is stale).
          locationSearch: state.location.search,
        };
      },
    });
  const search = acoContextSearchSchema.parse(locationSearch);
  // J-209 (inbox file 20 item 4): one guard for every screen that declares what it needs, so an account
  // the server refuses reads its permission state instead of "Loading." or a failed request.
  // J-251: a group or practice user reads the scope notice on every screen of ACO-wide data.
  const screenGate = screenAccess(role, requires, metadata, { kind: scopeKind, ownScope });

  // The obligations of a route change (22-COMPOUNDS-SHELL §2.1). None of them happens by itself on a
  // client-side navigation: the browser only does them on a document load. Keyed on the PATHNAME, not
  // on mount — this layout is pathless, so React keeps it mounted across every child-to-child
  // navigation and a `[]` dependency would run the obligations exactly once, ever.
  React.useEffect(() => {
    applyRouteChange({ screenName });
  }, [pathname, screenName]);

  async function signOut(search: { redirect: string | undefined; reason: "locked" | undefined }) {
    // J-353: a read this page still has in flight answers 401 once the session ends; it is this sign-out.
    await endingSession(async () => {
      await authClient.signOut();
      // J-243: the sign-in page clears the cache and router state once this layout has left the screen
      // (`features/auth/clear-user-state.ts`); clearing here made its mounted reads refetch without a session.
      await navigate({ to: "/login", search });
    });
  }
  const logout = () => signOut({ redirect: undefined, reason: undefined });
  // J-209 (inbox 06 PR D item 17): Lock session ends the session like Log out, and sign-in returns to
  // this page; the Playground is never a page to return to, as the guard above says.
  const lock = () =>
    signOut({ redirect: pathname === "/playground" ? undefined : href, reason: "locked" });

  return (
    // A1, the workspace archetype: the shell surrounds the content rather than the content carrying
    // its own chrome. Every part here is a @vbltc/ui shell component, which is also what satisfies
    // CDoD-11 — a component is only "reachable" once a real VBLTC screen uses it (D-107).
    // h-screen, not min-h-screen: the rail stretches to the row, and the row can only have a
    // definite height if the column above it does. With min-height the height is indefinite and
    // the rail stops at its own content (J-059).
    <div className="flex h-screen flex-col overflow-hidden">
      {theme === "" ? null : <style data-testid="shell-theme">{theme}</style>}
      <TopBar
        productName={appName}
        moduleName="Workspace"
        // Literal, and rendered whenever this is not production. Hiding it in a production-like
        // training system is the failure §4.2 exists to prevent: wrong-environment work.
        // VITE_APP_ENV is the variable this app already declares in vite-env.d.ts and reads in
        // telemetry.ts; a second name for the same fact would be one more thing to keep in step.
        environment={import.meta.env.VITE_APP_ENV ?? import.meta.env.MODE}
        // J-280 (QB3 item 6b): vbltc.com's icon, the favicon's source, in place of the initial.
        logo={{ src: productMarkUrl, alt: "VBLTC" }}
        // J-280 (QB3 item 6c): the bar shows the user's name (the email when the name is empty); the
        // open menu shows the email, the role in words and the access (CR-005 item 10's wording for an
        // ACO-set user) above Change password and Log out (SI-11, SP-14).
        user={{
          name: session.user.name,
          email: session.user.email,
          role: roleLabel(session.user.role),
          access: userAccessLabel(access.data),
        }}
        // J-209 (inbox 06 PR D item 17): no Search, Help or Notifications control, as nothing answers
        // them; the top bar draws each only when given its handler.
        onLockSession={() => void lock()}
        // J-245: Settings' password page, from every screen and for every role.
        onChangePassword={() => void navigate({ to: "/settings/password" })}
        onSignOut={() => void logout()}
        // J-362 (it replaced J-314's Learning Academy link): every signed-in user; the server serves the
        // Quick Start behind sign-in. The Academy itself stays at /academy/.
        quickReferenceHref="/quick-reference/VBLTC_Quality_Portal_Quick_Start.pdf"
      />
      {/* J-216: a named region, so the year and ACO selectors sit inside a landmark (axe `region`). */}
      {metadata.data ? (
        <section aria-label="Year and ACO">
          <AcoContextBar
            years={metadata.data.years}
            year={acoContext.year}
            onYearChange={acoContext.setYear}
            acos={headerAcos}
            // J-301 (QB4 item 3a, replacing J-251's no "All"): every user is offered "All"; for a group
            // or practice user it is every ACO of its own groups or practices, its own rows only (the
            // server narrows every read, and refuses every ACO-wide one).
            aco={acoContext.aco}
            onAcoChange={acoContext.setAco}
            bannerText={metadata.data.hasSyntheticData ? metadata.data.syntheticBannerText : null}
            // CR-005 item 11 (J-191): production's saves rebuild from the loaded bundle and its server
            // refuses the legacy calculation, so it never offers one.
            {...(metadata.data.calculationStale &&
            !isProduction(metadata.data) &&
            acoContext.year !== undefined &&
            can({ acoData: ["calculate"] })
              ? {
                  recalculate: {
                    onRecalculate: () => recalculate.mutate(acoContext.year as number),
                    pending: recalculate.isPending,
                    error: recalculate.error ? "The calculation failed; try again." : null,
                  },
                }
              : {})}
          />
        </section>
      ) : null}
      <div className="flex min-h-0 flex-1">
        <SideNav
          items={navItems}
          // Read from the path, not written as a literal: the mapping is path-to-id and the list's
          // order is not part of it.
          currentId={navItems.find((item) => item.href === pathname)?.id ?? "aco_summary"}
          collapsed={navCollapsed}
          onToggleCollapsed={() => setNavCollapsed((c) => !c)}
          // Router links, not plain anchors: an anchor performs a full document load, which discards
          // the session's in-memory state and re-runs every query on a same-app navigation.
          renderLink={({ href, children, ...rest }) =>
            // A screen not built yet: its label stays in place, but nothing leads to a missing page.
            unavailable.has(href) ? (
              <span {...rest} role="link" aria-disabled="true" data-testid="nav-unavailable">
                {children}
              </span>
            ) : (
              // J-209 (inbox 06 PR D item 1), J-215 (inbox 22 item 3): every link keeps the header's
              // year and ACO, ACO Summary's own ACO included.
              <Link to={href} search={navLinkSearch(href, pathname, search)} {...rest}>
                {children}
              </Link>
            )
          }
        />
        {/* J-216: the heading is inside `main`, so no content sits outside a landmark (axe `region`);
            the content below it scrolls on its own, as it did when the heading was outside. */}
        {/* J-249: min-w-0, or a wide table sets the column's minimum width and the window, not the
            table, runs past the right edge; the tables scroll sideways inside themselves instead. */}
        <main className="flex min-h-0 min-w-0 flex-1 flex-col">
          {/* One h1 for the document, named by the screen the outlet is showing. The screens do not
              render their own: two h1s would make the focus move in obligation 1 land on the wrong one. */}
          <PageHeader heading={screenName} />
          <div data-testid="shell-content" className="flex-1 overflow-y-auto px-6 pb-6">
            {switching ? (
              // J-232 (item 5a, B1): the loading state the moment the route changes, never the old
              // screen under the new URL.
              <div data-testid="route-loading" role="status">
                <ShellLoading />
              </div>
            ) : screenGate.kind === "open" ? (
              // J-376: the router's waiting view, drawn here while a page's code loads, is no second main.
              <InShellContext.Provider value={true}>
                <Outlet />
              </InShellContext.Provider>
            ) : (
              <ScreenAccessNotice
                access={screenGate}
                screen={screenName}
                onRetry={() => void access.refetch()}
              />
            )}
          </div>
        </main>
      </div>

      {/* Full width beneath the rail, as every archetype draws it (03- §2.3): the status region and
          the build marker belong to the window, not to the content column. Nesting them beside the
          rail left the rail ending short of the bottom, which is what the user saw. */}
      {/* J-210: while a request waits out a 429 the server is busy, and the page says so. */}
      <StatusRegion
        {...(busy.waiting > 0
          ? {
              tone: "moderate" as const,
              message: `The server is busy. Trying again in ${busy.seconds} seconds; you are still signed in.`,
            }
          : {})}
        data-testid="server-busy"
      />
      <BuildMarker
        release={build.release}
        sha={build.sha}
        environment={build.environment}
        pending={build.pending}
      />
      {warning && (
        <div
          role="alertdialog"
          aria-label="Still there?"
          className="bg-destructive/10 fixed inset-x-0 top-0 z-50 flex items-center justify-center gap-4 p-3 text-sm"
        >
          You will be signed out in one minute without activity.
          <Button size="sm" onClick={stayActive} data-telemetry="idle-stay">
            Stay signed in
          </Button>
        </div>
      )}
    </div>
  );
}
